import React, { useState, useEffect } from "react";
import FormRadioButton from "../../../components/formRadioButton";
import { YesNoOptions } from "../../../constants/selectData";
import SimpleTable from "../../../components/tables/simpleTable";
import { useLoadingState } from "../../../components/loadingFeedback";
import { icons } from "../../../constants";
import ModalComponent from "../../../components/modals";
import FormTextField from "../../../components/textField/formTextField";
import LaddaButton from "../../../components/buttons/laddaButton";
import { useForm } from "react-hook-form";
import { Grid, Box, Typography } from "@mui/material";
import MultiSelect from "../../../components/selects/multiSelect";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  callApiAsync,
  createOrUpdateSparePart,
  deleteEngineeringActSparePart,
  getEngineeringActSparePartsTable,
  getSpareParts
} from "../../../services/apiService";
import { useDispatch, useSelector } from "react-redux";
import { generateSelectList } from "../../../utils/utilsFunctions";
import VerificationModal from "../../../components/modals/verificationModal";
import ToastNotification from "../../../components/toastNotification";
import { setEngineeringReportId } from "../../../redux/slices/serviceOrderSlice";

const SparePartsUsedSection = ({
  dataER,
  onSparePartsUsedChange,
  isComplete,
  serviceOrderId,
  onPendingChangesChange
}) => {
  const dispatch = useDispatch();

  const [tableData, setTableData] = useState([]);
  const [loadingParts, setLoadingParts] = useLoadingState(true);
  const [openModal, setOpenModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [sparePartSelect, setSparePartSelect] = useState(null);
  const [sparePartsData, setSparePartsData] = useState(null);

  const [openVerification, setOpenVerification] = useState(false);
  const [selectedSparePartToDelete, setSelectedSparePartToDelete] =
    useState(null);

  const [showToast, setShowToast] = useState(false);
  const [toastConfig, setToastConfig] = useState({
    severity: "success",
    summary: "",
    detail: ""
  });

  const validationSchema = yup.object().shape({
    sparePart: yup.string().required("Este campo es obligatorio"),
    reference: yup.string().required("Este campo es obligatorio"),
    batch: yup.string().required("Este campo es obligatorio"),
    value: yup
      .string()
      .required("Este campo es obligatorio")
      .test("no-comma", "Usa punto (.) como separador decimal", (val) => !val?.includes(","))
      .test("is-valid-number", "Debe ser un valor positivo", (val) => {
        const num = parseFloat(val || "");
        return !isNaN(num) && num > 0;
      }),
  });
  
  const engineeringReportId = useSelector(
    (state) => state.serviceOrder.engineeringReportId
  );

  const {
    control,
    formState: { errors, dirtyFields },
    getValues,
    watch,
    setValue,
    reset,
    handleSubmit
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  const hasPendingChanges = openModal && Object.keys(dirtyFields).length > 0;
  useEffect(() => {
    onPendingChangesChange?.(hasPendingChanges);
  }, [hasPendingChanges, onPendingChangesChange]);

  const watchSparePartsUsed = watch("SparePartsUsed");
  const watchSpareParts = watch("sparePart");

  const handleOpenModal = (row) => {
    reset({ sparePart: "", reference: "", batch: "", value: "",
      SparePartsUsed: getValues("SparePartsUsed") });
    setSelectedActivity(row);
    setOpenModal(true);
  };

  const handleCloseModal = () => {
    setValue("sparePart", "");
    setValue("reference", "");
    setValue("batch", "");
    setValue("value", "");
    setOpenModal(false);
  };

  const loadData = () => {
    setValue("SparePartsUsed", dataER.has_spare_parts ? true : false);
  };

  const columns = [
    { field: "sparePartName", header: "Repuesto" },
    { field: "part_number", header: "Referencia" },
    { field: "batch", header: "Lote" },
    { field: "value", header: "Valor" },
    { field: "actionButtons", columnType: "actionButtons" }
  ];

  const getSparePartSelect = async () => {
    await callApiAsync(
      () => getSpareParts(),
      (data) => {
        const selectData = generateSelectList(data, "id", "name");
        setSparePartsData(data);
        setSparePartSelect(selectData);
      }
    );
  };

  const getActivitiesEngineeringTable = async () => {
    await callApiAsync(
      () => getEngineeringActSparePartsTable(engineeringReportId),
      (data) => {
        setTableData(data);
        if (data.length > 0) {
          setValue("SparePartsUsed", true);
        }
      },
      setLoadingParts
    );
  };

  const confirmDeleteSparePart = (data) => {
    setSelectedSparePartToDelete(data);
    setOpenVerification(true);
  };

  const handleDeleteConfirmed = async () => {
    if (!selectedSparePartToDelete) return;

    await callApiAsync(
      () => deleteEngineeringActSparePart(selectedSparePartToDelete.id),
      () => {
        getActivitiesEngineeringTable();
        setToastConfig({
          severity: "info",
          summary: "Repuesto eliminado",
          detail: "El repuesto fue eliminado correctamente."
        });
        setShowToast(true);
        setOpenVerification(false);
        setSelectedSparePartToDelete(null);
      }
    );
  };

  const onSubmit = async (formData) => {
    const dataToSend = {
      id: selectedActivity ? selectedActivity.id : 0,
      engineeringActId: engineeringReportId,
      spare_part_id: formData.sparePart,
      batch: formData.batch,
      service_order_id: serviceOrderId,
      value: formData.value,
      made_by_user_id: dataER
        ? dataER.made_by_user_id || dataER.engineer_id || null
        : null
    };
    await callApiAsync(
      () => createOrUpdateSparePart(dataToSend),
      (data) => {
        dispatch(setEngineeringReportId(data.engineeringActId));
        handleCloseModal();
        getActivitiesEngineeringTable();
        setToastConfig({
          severity: "success",
          summary: selectedActivity
            ? "Repuesto actualizado"
            : "Repuesto agregado",
          detail: "La operación se realizó correctamente."
        });
        setShowToast(true);
      }
    );
  };

  useEffect(() => {
    getActivitiesEngineeringTable();
    getSparePartSelect();
  }, [engineeringReportId]);

  useEffect(() => {
    if (!dataER) return;
    loadData();
  }, [dataER]);

  useEffect(() => {
    if (onSparePartsUsedChange) {
      onSparePartsUsedChange(watchSparePartsUsed);
    }
  }, [watchSparePartsUsed]);

  useEffect(() => {
    if (!selectedActivity) return;
    setValue("sparePart", selectedActivity.spare_part_id);
    setValue("batch", selectedActivity.batch);
    setValue("value", selectedActivity.value);
  }, [selectedActivity]);

  useEffect(() => {
    if (!watchSpareParts) return;

    const selectedPart = sparePartsData.find(
      (part) => part.id === watchSpareParts
    );
    if (selectedPart) {
      setValue("reference", selectedPart.part_number);
      setValue("value", selectedPart.value);
    }
  }, [watchSpareParts]);

  return (
    <div>
      <FormRadioButton
        name="SparePartsUsed"
        control={control}
        onChange={() => console.log("yes")}
        options={YesNoOptions}
        margin={"0"}
        disabled={isComplete}
      />
      {watchSparePartsUsed === true && (
        <div className="mt-20">
          {!isComplete && (
            <div className="j-end">
              <LaddaButton
                variant="contained"
                onClick={() => {
                  handleOpenModal();
                }}
              >
                <div className="IHContainer align-rigth">
                  <img
                    className="btn-icon"
                    src={icons.plusIcon}
                    alt="icon"
                    style={{ marginRight: "10px" }}
                  />
                  <p className="button_text">Agregar Repuesto</p>
                </div>
              </LaddaButton>
            </div>
          )}
          <SimpleTable
            loading={loadingParts}
            data={tableData}
            columns={columns}
            text={"Realizar Autoevaluación"}
            isPaginated={true}
            sortable
            buttons={
              !isComplete
                ? [
                    {
                      type: "icon",
                      icon: icons.editBlue,
                      onClick: (data) => handleOpenModal(data)
                    },
                    {
                      type: "icon",
                      icon: icons.closeRed,
                      onClick: (data) => confirmDeleteSparePart(data)
                    }
                  ]
                : []
            }
          />
        </div>
      )}

      <ModalComponent
        open={openModal}
        handleClose={handleCloseModal}
        width="560px"
        title={
          selectedActivity != null ? "Editar Repuesto" : "Agregar Repuesto"
        }
        actions={<></>}
      >
        <Box sx={{ pt: 1, pb: 2, px: 0 }}>
          <form onSubmit={handleSubmit(onSubmit)}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={6}>
                <Typography
                  component="label"
                  variant="body2"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: "#374151",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  Repuesto
                </Typography>
                <MultiSelect
                  name="sparePart"
                  control={control}
                  label=""
                  options={sparePartSelect || []}
                  margin="0"
                  error={errors.sparePart}
                  isMultiple={false}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  component="label"
                  variant="body2"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: "#374151",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  Referencia
                </Typography>
                <FormTextField
                  name="reference"
                  control={control}
                  label=""
                  error={errors.reference}
                  disabled
                  margin="0"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  component="label"
                  variant="body2"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: "#374151",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  Lote
                </Typography>
                <FormTextField
                  name="batch"
                  control={control}
                  label=""
                  error={errors.batch}
                  margin="0"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography
                  component="label"
                  variant="body2"
                  sx={{
                    display: "block",
                    mb: 1,
                    color: "#374151",
                    fontWeight: 500,
                    fontSize: "0.875rem"
                  }}
                >
                  Valor
                </Typography>
                <FormTextField
                  name="value"
                  control={control}
                  label=""
                  error={errors.value}
                  margin="0"
                />
              </Grid>
            </Grid>
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                marginTop: 4,
                paddingTop: 2
              }}
            >
              <LaddaButton type="submit" variant="contained">
                <p className="button_text">
                  {selectedActivity != null ? "Guardar" : "Agregar"}
                </p>
              </LaddaButton>
            </Box>
          </form>
        </Box>
      </ModalComponent>

      <VerificationModal
        open={openVerification}
        handleClose={() => setOpenVerification(false)}
        alertText="¿Estás seguro de eliminar este repuesto?"
        onConfirm={handleDeleteConfirmed}
      />

      <ToastNotification
        severity={toastConfig.severity}
        summary={toastConfig.summary}
        detail={toastConfig.detail}
        showToast={showToast}
        setShowToast={setShowToast}
      />
    </div>
  );
};

export default SparePartsUsedSection;
